// AttributionUnlinkControls — the shared delivered-row management affordance.
//
// One implementation of "re-attribute a delivered account to a different agency"
// + "unlink a row from its request", reused by BOTH the account-request detail
// drawer (AccountRequestsPage) and the Accounts hub drawer (AccountDrawer).
//
//   Change agency -> POST /admin/accounts/{id}/attribution (ALL agencies)
//   Unlink        -> POST /admin/accounts/{id}/unlink (confirm-gated)
//
// "Assign once -> works everywhere": re-attributing ALSO grants the owning
// client proxy access by default (the append_allowlist opt-in — explicit +
// audited, backend D12). The grant checkbox is default-checked and only shown
// when the row actually has an owning client. PLACEHOLDER rows (is_placeholder,
// no real advertiser id) have nothing to re-attribute -> they offer Unlink only.
//
// The callbacks return the updated AccountRow so the caller can keep its own
// list/drawer state in sync (the requests page drops the row from the delivered
// list; the accounts hub swaps it in place with request_id cleared).

import { useState } from "react";
import { toast } from "sonner";

import {
  setAccountAttribution,
  unlinkAccount,
  type AccountRow,
} from "@/api/accounts";
import { errMessage } from "@/lib/errMessage";
import { Button } from "@/components/ui/button";
import { NativeSelect } from "@/routes/nexus/controls";

interface AgencyRef {
  id: string;
  name: string;
}

export function AttributionUnlinkControls({
  account,
  agencies,
  agencyName,
  onUpdated,
  onUnlinked,
}: {
  account: AccountRow;
  agencies: AgencyRef[];
  agencyName: (id: string) => string;
  onUpdated: (row: AccountRow) => void;
  onUnlinked: (row: AccountRow) => void;
}) {
  const [mode, setMode] = useState<"idle" | "agency" | "unlink">("idle");
  const [agencySel, setAgencySel] = useState(account.agency_id);
  const [busy, setBusy] = useState(false);
  // "Assign once -> works everywhere": re-attributing also grants the owning
  // client proxy access by default (explicit + audited — backend D12). Only
  // meaningful when the row actually has an owning client.
  const canAppendAllowlist = account.client_id !== "";
  const [appendAllowlist, setAppendAllowlist] = useState(true);

  const label = account.account_id || account.id;

  async function applyAgency() {
    if (!agencySel) return;
    setBusy(true);
    try {
      const updated = await setAccountAttribution(account.id, agencySel, {
        append_allowlist: canAppendAllowlist && appendAllowlist,
      });
      toast.success(
        `Re-attributed to ${agencyName(updated.agency_id)}${
          canAppendAllowlist && appendAllowlist
            ? " — added to the client's proxy allowlist"
            : ""
        }`,
      );
      setMode("idle");
      onUpdated(updated);
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function confirmUnlink() {
    setBusy(true);
    try {
      const updated = await unlinkAccount(account.id);
      toast.success("Account unlinked from this request");
      onUnlinked(updated);
    } catch (err) {
      toast.error(errMessage(err));
      setBusy(false);
    }
  }

  return (
    <>
      {mode === "idle" && (
        <div className="mt-1.5 flex gap-2">
          {!account.is_placeholder && (
            <Button
              variant="ghost"
              size="xs"
              aria-label={`Change agency ${label}`}
              onClick={() => {
                setAgencySel(account.agency_id);
                setAppendAllowlist(true);
                setMode("agency");
              }}
            >
              Change agency
            </Button>
          )}
          <Button
            variant="ghost"
            size="xs"
            aria-label={`Unlink ${label}`}
            className="text-nx-debit hover:text-nx-debit"
            onClick={() => setMode("unlink")}
          >
            Unlink
          </Button>
        </div>
      )}

      {mode === "agency" && (
        <div className="mt-1.5 grid gap-1.5">
          <div className="flex items-center gap-2">
            <NativeSelect
              aria-label={`New agency for ${label}`}
              value={agencySel}
              onChange={(e) => setAgencySel(e.target.value)}
              className="h-7 flex-1 text-xs"
            >
              {agencies.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </NativeSelect>
            <Button
              size="xs"
              disabled={busy || !agencySel || agencySel === account.agency_id}
              onClick={applyAgency}
            >
              Apply
            </Button>
            <Button variant="ghost" size="xs" onClick={() => setMode("idle")}>
              Cancel
            </Button>
          </div>
          {/* Default-checked "assign once -> works everywhere" grant — an
              explicit, audited proxy-allowlist merge (backend D12). */}
          {canAppendAllowlist && (
            <label className="flex items-start gap-2 text-xs text-nx-text-2">
              <input
                type="checkbox"
                checked={appendAllowlist}
                onChange={(e) => setAppendAllowlist(e.target.checked)}
                className="mt-0.5 accent-nx-brand"
              />
              <span>
                Also add to this client&apos;s proxy allowlist (so the client can
                see &amp; recharge it)
              </span>
            </label>
          )}
        </div>
      )}

      {mode === "unlink" && (
        <div className="mt-1.5 flex items-center justify-between gap-2">
          <p className="text-xs text-nx-text-2">
            Remove from this request? The account row is kept; the request is
            not recomputed.
          </p>
          <div className="flex shrink-0 gap-2">
            <Button
              variant="destructive"
              size="xs"
              disabled={busy}
              aria-label={`Confirm unlink ${label}`}
              onClick={confirmUnlink}
            >
              Confirm unlink
            </Button>
            <Button variant="ghost" size="xs" onClick={() => setMode("idle")}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
